import { ReactElement } from 'react';
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

// Components
import { WavyLine } from '@/components/common/WavyLine';

// Constants
import {
  DEFAULT_ENTRY_ANIMATION,
  DEFAULT_LINE_STYLE,
  DEFAULT_WAVE_CONFIG,
} from '@/components/common/WavyLine/constants';
import { CLAMP_BOTH } from '@/constants';

// Types
import {
  EntryAnimation,
  LineConfig,
  LineStyle,
  WaveConfig,
} from '@/components/common/WavyLine/types';

// Utils
import { combineClasses } from '@/utils';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { lalezarFontFamily } from '@/styles/fonts';

/**
 * Configuration for text entrance animation
 */
export interface TextEntranceConfig {
  startFrame?: number;
  charStagger?: number;
  slideDistance?: number;
}

/**
 * Props for WavyLines component
 */
export interface WavyLinesProps {
  /** Optional CSS class name */
  className?: string;
  /** Canvas width (default: 1920) */
  width?: number;
  /** Canvas height (default: 1080) */
  height?: number;
  /** Configuration for wave animation */
  waveConfig?: Partial<WaveConfig>;
  /** Configuration for line styling */
  lineStyle?: Partial<LineStyle>;
  /** Configuration for entry animation */
  entryAnimation?: Partial<EntryAnimation>;
  /** Single line configuration (optional, uses default if not provided) */
  line?: LineConfig;
  /** Rotation speed in degrees per frame (default: 0.1) */
  rotationSpeed?: number;
  /** Padding around SVG to prevent clipping during rotation (default: 800) */
  svgPadding?: number;
  /** Overall opacity of the lines container (default: 0.2) */
  containerOpacity?: number;
  /** Text entrance animation configuration. When provided, characters animate in with a stagger. */
  textEntrance?: TextEntranceConfig;
}

/**
 * WavyLines component: Renders animated wavy lines with continuous wave motion
 * and slow rotation. Highly configurable for reuse in different contexts.
 */
export const WavyLinesBackground = ({
  className,
  width = 1920,
  height = 1080,
  waveConfig: waveConfigProp,
  lineStyle: lineStyleProp,
  entryAnimation: entryAnimationProp,
  line: lineProp,
  rotationSpeed = 0.1,
  svgPadding = 800,
  containerOpacity = 0.15,
  textEntrance,
}: WavyLinesProps): ReactElement => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { typography, rawColors } = employeeYearInReviewTheme;

  // Text entrance defaults
  const entranceStartFrame = textEntrance?.startFrame ?? 0;
  const charStagger = textEntrance?.charStagger ?? 2;
  const slideDistance = textEntrance?.slideDistance ?? 40;

  // Merge provided config with defaults
  const waveConfig: WaveConfig = {
    ...DEFAULT_WAVE_CONFIG,
    ...waveConfigProp,
  };

  const lineStyle: LineStyle = {
    ...DEFAULT_LINE_STYLE,
    ...lineStyleProp,
  };

  const entryAnimation: EntryAnimation = {
    ...DEFAULT_ENTRY_ANIMATION,
    ...entryAnimationProp,
  };

  const lineConfig = lineProp || {
    startX: width - 200 + svgPadding,
    startY: -300 + svgPadding,
    endX: width * 0.5 + svgPadding,
    endY: height + 700 + svgPadding,
    wavePhaseOffset: Math.PI,
    entryDelay: 8,
  };

  // Extended dimensions to prevent clipping during rotation
  const extendedWidth = width + svgPadding * 2;
  const extendedHeight = height + svgPadding * 2;

  // Continuous 360-degree rotation animation
  const rotation = (frame * rotationSpeed) % 360;

  return (
    <AbsoluteFill
      className={className}
      style={{
        overflow: 'hidden',
        opacity: containerOpacity,
      }}
    >
      {/* SVG wavy lines - rendered at full opacity */}
      <svg
        width={extendedWidth}
        height={extendedHeight}
        viewBox={`0 0 ${extendedWidth} ${extendedHeight}`}
        style={{
          position: 'absolute',
          top: -svgPadding,
          left: -svgPadding,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center center',
          overflow: 'visible',
        }}
      >
        <WavyLine
          {...lineConfig}
          frame={frame}
          waveConfig={waveConfig}
          lineStyle={lineStyle}
          entryAnimation={entryAnimation}
        />
      </svg>

      {/* Text - rendered at full opacity with subtle wave animation */}
      <div className="pt-16 pl-28">
        {['FEEDBACK', 'HIGHLIGHT'].map((line, lineIndex) => (
          <p
            key={`wave-line-${lineIndex}`}
            className={combineClasses(typography.xl, 'uppercase', {
              '-mt-12.5': lineIndex > 0, // Add margin-top to the second line
            })}
            style={{
              fontFamily: lalezarFontFamily,
              color: rawColors.wavyLinesBackground.backdropText,
            }}
          >
            {line.split('').map((char, charIndex) => {
              // Continuous character index for smooth wave across lines
              const globalIndex = lineIndex * 10 + charIndex;
              // Subtle wave animation using same pattern as Flip3DBadge floatOffset
              const wavePhase = globalIndex * 0.3; // Stagger phase for wave effect
              const waveOffset = Math.sin(frame * 0.08 + wavePhase) * 5; // Larger amplitude

              // Character entrance animation (only when textEntrance is provided)
              let entranceOpacity = 1;
              let entranceTranslateY = 0;

              if (textEntrance) {
                const charDelay =
                  entranceStartFrame + globalIndex * charStagger;

                const charProgress = spring({
                  frame: frame - charDelay,
                  fps,
                  config: { damping: 200, mass: 0.5, stiffness: 80 },
                });

                entranceOpacity = interpolate(
                  charProgress,
                  [0, 0.6],
                  [0, 1],
                  CLAMP_BOTH,
                );

                entranceTranslateY = interpolate(
                  charProgress,
                  [0, 1],
                  [slideDistance, 0],
                  CLAMP_BOTH,
                );
              }

              return (
                <span
                  key={`wave-char-${lineIndex}-${charIndex}`}
                  className="inline-block"
                  style={{
                    opacity: entranceOpacity,
                    transform: `translateY(${waveOffset + entranceTranslateY}px)`,
                  }}
                >
                  {char}
                </span>
              );
            })}
          </p>
        ))}
      </div>
    </AbsoluteFill>
  );
};
